import type { ChartEventMarker } from "./LineChart";

interface AxisRow {
  label?: string | number | null;
  period?: string | number | null;
  period_start?: string | number | null;
  period_end?: string | number | null;
  [key: string]: unknown;
}

/**
 * Maps ISO-dated items (client events, weather anomalies) onto the chart's
 * categorical x-axis. Each chart bucket spans [period_start, period_end]; an
 * item falls in the bucket whose span contains its date. Returns one marker per
 * matched item, placed at that bucket's axis value (the detected x-axis key —
 * `label`, falling back to `period`).
 *
 * Items whose date lies outside every bucket (e.g. outside the visible range)
 * are dropped so no orphan marker is rendered.
 */
export const mapDatesToAxis = <T extends { date: string }>(
  rows: AxisRow[],
  items: T[],
  toMarker: (item: T, axisValue: string) => ChartEventMarker,
): ChartEventMarker[] => {
  if (!rows.length || !items.length) return [];

  const buckets = rows.map((r) => ({
    axisValue: String(r.label ?? r.period ?? ""),
    start: r.period_start ? String(r.period_start).slice(0, 10) : null,
    end: r.period_end ? String(r.period_end).slice(0, 10) : null,
  }));

  const markers: ChartEventMarker[] = [];
  for (const item of items) {
    const day = item.date.slice(0, 10);
    const bucket = buckets.find((b) => {
      if (b.start && b.end) return day >= b.start && day <= b.end;
      // No explicit bounds — match the bucket whose axis value equals the day.
      return b.axisValue.slice(0, 10) === day;
    });
    if (bucket && bucket.axisValue) {
      markers.push(toMarker(item, bucket.axisValue));
    }
  }
  return markers;
};

/**
 * Maps a dated range [startDate, endDate] onto the chart's categorical x-axis,
 * returning the first and last bucket axis values the range overlaps. Clamps to
 * the visible window, so a range that begins before or ends after the chart
 * still yields a band spanning the visible portion. Returns null if the range
 * does not overlap any bucket.
 */
export const mapRangeToAxis = (
  rows: AxisRow[],
  startDate: string,
  endDate: string,
): { startAxisValue: string; endAxisValue: string } | null => {
  if (!rows.length) return null;

  const buckets = rows.map((r) => ({
    axisValue: String(r.label ?? r.period ?? ""),
    start: r.period_start ? String(r.period_start).slice(0, 10) : null,
    end: r.period_end ? String(r.period_end).slice(0, 10) : null,
  }));

  const rangeStart = startDate.slice(0, 10);
  const rangeEnd = endDate.slice(0, 10);

  const overlapping = buckets.filter((b) => {
    const bStart = b.start ?? b.axisValue.slice(0, 10);
    const bEnd = b.end ?? b.axisValue.slice(0, 10);
    // Bucket overlaps the range if it starts on/before the range end and ends
    // on/after the range start.
    return bStart <= rangeEnd && bEnd >= rangeStart;
  });

  if (!overlapping.length) return null;
  return {
    startAxisValue: overlapping[0].axisValue,
    endAxisValue: overlapping[overlapping.length - 1].axisValue,
  };
};
